import type { FC, ReactNode } from 'react'
import type { AuthorProfile } from '@/lib/content/types'

import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'
import { cmsKey } from '@/lib/portal/cms'
import { AuthorAvatar, field } from '@/components/site/article-meta'
import { useLocale } from '@/i18n/use-locale'
import { siteContent } from '@/i18n/site-content'

interface ArticleAuthorProps {
    author: AuthorProfile
    className?: string
}

/** The author box at the end of an article. */
export const ArticleAuthor: FC<ArticleAuthorProps> = ({ author, className }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale].article
    const role = field(author, 'role', locale)
    const bio = field(author, 'bio', locale)

    return (
        <section aria-labelledby={`author-${author.id}`} className={cn('relative overflow-hidden rounded-3xl border border-border bg-mist p-7 sm:p-9', className)}>
            <div className='bg-grid mask-radial pointer-events-none absolute inset-0 opacity-50' aria-hidden='true' />

            <div className='relative'>
                <p className='text-xs font-semibold uppercase tracking-[0.18em] text-primary'>
                    {copy.aboutAuthor}
                </p>

                <div className='mt-6 flex flex-col gap-5 sm:flex-row sm:items-start'>
                    <AuthorAvatar author={author} className='size-16 text-lg' />

                    <div className='min-w-0'>
                        <h2 id={`author-${author.id}`} className='font-display text-[1.8rem] leading-tight text-foreground'>
                            <span data-mdlfy-cms={cmsKey('authors', author.id, 'name')}>{author.name}</span>
                        </h2>

                        {role.value && (
                            <p className='mt-1 text-sm font-medium text-muted-foreground' data-mdlfy-cms={cmsKey('authors', author.id, role.column)}>
                                {role.value}
                            </p>
                        )}

                        {bio.value && (
                            <p className='mt-4 text-[0.98rem] leading-relaxed text-foreground/80' data-mdlfy-cms={cmsKey('authors', author.id, bio.column)}>
                                {bio.value}
                            </p>
                        )}

                        {author.linkedin_url && (
                            <a
                                href={author.linkedin_url}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline'
                            >
                                {copy.linkedin}
                                <ExternalLink className='size-4' aria-hidden='true' />
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
